import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import {
  formatPlainTextToMarkdown,
  isMarkdownFormatted,
} from "../../utils/textFormatter";
import styles from "./markdown.module.css";

interface MarkdownRendererProps {
  content: string;
  autoFormat?: boolean; // Auto-format plain text to markdown
  onLinkClick?: (url: string) => void; // Callback for link clicks (for tracking)
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  autoFormat = true,
  onLinkClick,
}) => {
  // Normalize unicode bullet points (•) to standard markdown list items (-)
  const normalizedContent = content.replace(/^•\s*/gm, "- ");

  // Auto-format plain text if enabled and content is not already markdown
  const formattedContent =
    autoFormat && !isMarkdownFormatted(normalizedContent)
      ? formatPlainTextToMarkdown(normalizedContent)
      : normalizedContent;

  return (
    <div className={styles.markdown}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={
          {
            // Customize heading styles
            h1: ({ node, ...props }) => <h1 className={styles.h1} {...props} />,
            h2: ({ node, ...props }) => <h2 className={styles.h2} {...props} />,
            h3: ({ node, ...props }) => <h3 className={styles.h3} {...props} />,
            h4: ({ node, ...props }) => <h4 className={styles.h4} {...props} />,
            h5: ({ node, ...props }) => <h5 className={styles.h5} {...props} />,
            h6: ({ node, ...props }) => <h6 className={styles.h6} {...props} />,

            // Customize list styles
            ul: ({ node, ...props }) => <ul className={styles.ul} {...props} />,
            ol: ({ node, ...props }) => <ol className={styles.ol} {...props} />,
            li: ({ node, ...props }) => <li className={styles.li} {...props} />,

            // Customize paragraph
            p: ({ node, children, ...props }) => {
              const extractText = (c: React.ReactNode): string =>
                React.Children.toArray(c).map(child =>
                  typeof child === "string" ? child :
                  React.isValidElement(child) ? extractText((child.props as any).children) : ""
                ).join("");
              const text = extractText(children);
              if (/ask a question if you need more information/i.test(text)) return null;
              if (/download technical evaluation workbook/i.test(text)) return null;
              return <p className={styles.p} {...props}>{children}</p>;
            },

            // Customize code blocks
            code: ({ node, className, children, ...props }) => {
              const match = /language-(\w+)/.exec(className || "");
              const isInline = !match && !className;
              return isInline ? (
                <code className={styles.inlineCode} {...props}>
                  {children}
                </code>
              ) : (
                <code className={styles.codeBlock} {...props}>
                  {children}
                </code>
              );
            },
            pre: ({ node, children, ...props }) => {
              const codeChild = React.Children.toArray(children)[0] as React.ReactElement<any>;
              const text = codeChild?.props?.children
                ? String(codeChild.props.children).trim()
                : "";
              if (/^https?:\/\//.test(text)) {
                return null;
              }
              return <pre className={styles.pre} {...props}>{children}</pre>;
            },

            // Customize blockquote
            blockquote: ({ node, ...props }) => (
              <blockquote className={styles.blockquote} {...props} />
            ),

            // Customize table
            table: ({ node, ...props }) => (
              <table className={styles.table} {...props} />
            ),
            thead: ({ node, ...props }) => (
              <thead className={styles.thead} {...props} />
            ),
            tbody: ({ node, ...props }) => (
              <tbody className={styles.tbody} {...props} />
            ),
            tr: ({ node, ...props }) => <tr className={styles.tr} {...props} />,
            th: ({ node, ...props }) => <th className={styles.th} {...props} />,
            td: ({ node, ...props }) => <td className={styles.td} {...props} />,

            // Customize links
            a: ({ node, href, ...props }) => {
              const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                if (onLinkClick && href) {
                  onLinkClick(href);
                }
              };

              return (
                <a
                  className={styles.link}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClick}
                  {...props}
                />
              );
            },

            // Customize strong/bold
            strong: ({ node, ...props }) => (
              <strong className={styles.strong} {...props} />
            ),

            // Customize emphasis/italic
            em: ({ node, children, ...props }) => {
              const text = String(children ?? "");
              if (/click or copy the url/i.test(text)) return null;
              return <em className={styles.em} {...props}>{children}</em>;
            },
          } as Components
        }
      >
        {formattedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
